import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import LPBlocksEditor from "../components/LPBlocksEditor";
import { Plus, Edit, Trash2, Save, ArrowLeft, ExternalLink, Copy, Eye, Sparkles } from "lucide-react";
import { PLACES_PREMIUM_TEMPLATE } from "../../data/lp_templates";
import PlacesLogo from "@/components/places/PlacesLogo";

/** "#FF5E00, #00B247" → ["#FF5E00","#00B247"] · vazio = paleta padrão da marca. */
function parseColors(v) {
  if (!v) return undefined;
  const arr = String(v).split(",").map((s) => s.trim()).filter(Boolean);
  return arr.length ? arr : undefined;
}

const EMPTY = {
  slug: "",
  title: "",
  description: "",
  published: false,
  noindex: false,
  meta_title: "",
  meta_description: "",
  og_image: "",
  favicon: "",
  primary_color: "",
  ga_id: "",
  meta_pixel_id: "",
  gtm_id: "",
  notify_emails: "",
  utm_default: {},
  sticky_cta: { enabled: false, label: "", scroll_percent: 30, whatsapp_phone: "", whatsapp_message: "" },
  exit_intent: { enabled: false, delay_seconds: 45, scroll_percent: 40, title: "", subtitle: "", cta_label: "" },
  header: { enabled: true, brand_color: "white", link: "/" },
  chatbot: { enabled: false, whatsapp_number: "", whatsapp_message_template: "", delay_seconds: "" },
  blocks: [],
};

/* ============ List ============ */
export function LandingPagesList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/admin/collection/landing_pages")
      .then(({ data }) => setItems(data))
      .finally(() => setLoading(false));
  }, []);
  const remove = async (id) => {
    if (!window.confirm("Excluir esta LP? Ela sai do ar imediatamente.")) return;
    await api.delete(`/admin/landing_pages/${id}`);
    setItems(items.filter(i => i.id !== id));
    toast.success("Excluído.");
  };
  const copyUrl = (slug) => {
    const url = `${window.location.origin}/lp/${slug}`;
    navigator.clipboard?.writeText(url);
    toast.success("URL copiada para a área de transferência.");
  };
  return (
    <div>
      <div className="flex justify-between items-end mb-6">
        <div>
          <h1 className="font-display text-4xl">Landing Pages</h1>
          <p className="text-sm text-neutral-500 mt-1">Páginas dedicadas de alta conversão para campanhas. URL: <code>/lp/&lt;slug&gt;</code></p>
        </div>
        <Link to="/admin/landing-pages/new" className="bg-accent-yellow text-black text-sm font-semibold px-4 py-2 rounded-full" data-testid="lp-new"><Plus className="w-4 h-4 inline mr-1" /> Nova LP</Link>
      </div>
      {loading ? (
        <div className="text-sm text-neutral-500 text-center py-12">Carregando…</div>
      ) : (
        <div className="space-y-2" data-testid="lp-list">
          {items.map((lp) => (
            <div key={lp.id} className="flex items-center gap-4 border border-black/10 p-3 rounded-md hover:bg-black/[0.02]">
              <div className={`w-2 h-10 rounded-full ${lp.published ? "bg-green-500" : "bg-neutral-300"}`} title={lp.published ? "Publicada" : "Rascunho"} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{lp.title || <span className="text-neutral-400">Sem título</span>}</div>
                <div className="text-xs text-neutral-500 font-mono">/lp/{lp.slug}</div>
              </div>
              <span className="text-[10px] uppercase font-semibold tracking-widest text-neutral-500">
                {lp.blocks?.length || 0} blocos
              </span>
              <button onClick={() => copyUrl(lp.slug)} title="Copiar URL"
                className="text-xs border border-black/15 px-3 py-1.5 rounded flex items-center gap-1">
                <Copy className="w-3 h-3" />
              </button>
              <a href={`/lp/${lp.slug}`} target="_blank" rel="noopener noreferrer" title="Ver ao vivo"
                className="text-xs border border-black/15 px-3 py-1.5 rounded flex items-center gap-1">
                <Eye className="w-3 h-3" />
              </a>
              <Link to={`/admin/landing-pages/${lp.id}`} className="text-xs border border-black/15 px-3 py-1.5 rounded"><Edit className="w-3 h-3 inline" /></Link>
              <button onClick={() => remove(lp.id)} className="text-xs text-red-500 px-3 py-1.5"><Trash2 className="w-3 h-3 inline" /></button>
            </div>
          ))}
          {!items.length && <div className="text-sm text-neutral-500 text-center py-12">Nenhuma landing page ainda. Crie uma para começar.</div>}
        </div>
      )}
    </div>
  );
}

/* ============ Editor ============ */
export function LandingPageEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  const [item, setItem] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [showSubs, setShowSubs] = useState(false);

  useEffect(() => {
    if (isNew) return;
    api.get(`/content/landing_pages/${id}`).then(({ data }) => {
      setItem({ ...EMPTY, ...data });
    });
  }, [id, isNew]);

  const upd = (k, v) => setItem((s) => ({ ...s, [k]: v }));

  const loadTemplate = () => {
    if (item.blocks?.length && !window.confirm("Isso vai SOBRESCREVER todos os blocos atuais. Continuar?")) return;
    // Deep-clone template — cada LP tem sua própria cópia editável
    const t = JSON.parse(JSON.stringify(PLACES_PREMIUM_TEMPLATE));
    setItem((s) => ({
      ...s,
      title: s.title || t.title,
      description: s.description || t.description,
      meta_title: s.meta_title || t.meta_title,
      meta_description: s.meta_description || t.meta_description,
      og_image: s.og_image || t.og_image,
      sticky_cta: t.sticky_cta,
      exit_intent: t.exit_intent,
      header: t.header || s.header,
      blocks: t.blocks,
    }));
    toast.success("Template Places Premium carregado. Personalize e salve.");
  };

  const loadSubmissions = async () => {
    if (!item.slug) return;
    const { data } = await api.get(`/admin/lp-submissions/${item.slug}`);
    setSubmissions(data);
    setShowSubs(true);
  };

  const save = async () => {
    if (!item.slug || !item.title) {
      toast.error("Slug e título são obrigatórios.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...item };
      if (isNew) {
        const { data } = await api.post("/admin/landing_pages", payload);
        toast.success("LP criada.");
        navigate(`/admin/landing-pages/${data.id}`);
      } else {
        await api.put(`/admin/landing_pages/${id}`, payload);
        toast.success("LP salva.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="lp-editor">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <Link to="/admin/landing-pages" className="text-sm border border-black/15 px-3 py-1.5 rounded flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Voltar
          </Link>
          <h1 className="font-display text-3xl">{isNew ? "Nova Landing Page" : "Editar LP"}</h1>
          {item.published && <span className="text-[10px] uppercase font-semibold tracking-widest bg-green-100 text-green-800 px-2 py-1 rounded-full">Publicada</span>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadTemplate} data-testid="lp-load-template"
            className="text-xs bg-gradient-to-r from-accent-orange to-accent-yellow text-black font-semibold px-4 py-2 rounded-full flex items-center gap-1.5 hover:shadow-lg transition-shadow"
            title="Popular a LP com o template Places Premium (~13 blocos com copy pronta)">
            <Sparkles className="w-3.5 h-3.5" /> Carregar template Premium
          </button>
          {!isNew && (
            <>
              <button onClick={loadSubmissions} className="text-xs border border-black/15 px-3 py-2 rounded">Submissions</button>
              <a href={`/lp/${item.slug}`} target="_blank" rel="noopener noreferrer"
                className="text-xs border border-black/15 px-3 py-2 rounded flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> Preview
              </a>
            </>
          )}
          <button onClick={save} disabled={saving} data-testid="lp-save"
            className="bg-black text-white text-sm px-5 py-2.5 rounded-full hover:bg-accent-orange disabled:opacity-50">
            <Save className="w-3 h-3 inline mr-1" /> {saving ? "Salvando…" : "Salvar"}
          </button>
        </div>
      </div>

      {showSubs && (
        <div className="border border-black/10 rounded-md p-4 mb-6 bg-neutral-50">
          <div className="flex justify-between items-center mb-3">
            <div className="text-sm uppercase font-semibold tracking-widest text-neutral-700">Submissions ({submissions.length})</div>
            <button onClick={() => setShowSubs(false)} className="text-xs text-neutral-500">Fechar</button>
          </div>
          {submissions.length === 0 && <div className="text-xs text-neutral-500">Nenhuma submissão ainda.</div>}
          <div className="space-y-2 max-h-96 overflow-auto">
            {submissions.map((s) => (
              <div key={s.id} className="bg-white border border-black/10 p-3 rounded text-xs">
                <div className="flex justify-between mb-1">
                  <span className="font-mono">{s.created_at?.slice(0, 16)}</span>
                  <span className="text-neutral-500">{s.form_id}</span>
                </div>
                <pre className="whitespace-pre-wrap text-[11px]">{JSON.stringify(s.values, null, 2)}</pre>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Basics */}
      <div className="border border-black/10 rounded-md p-4 mb-4 space-y-3">
        <div className="text-sm uppercase tracking-widest font-semibold text-neutral-700 mb-1">Identificação</div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-neutral-500">Slug (URL) *</label>
            <div className="flex items-center gap-1">
              <span className="text-xs text-neutral-400 font-mono">/lp/</span>
              <input value={item.slug} onChange={(e) => upd("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                data-testid="lp-slug"
                placeholder="ex: campanha-black-friday"
                className="flex-1 border border-black/15 px-3 py-2 rounded text-sm font-mono" />
            </div>
          </div>
          <div>
            <label className="text-xs text-neutral-500">Título interno *</label>
            <input value={item.title} onChange={(e) => upd("title", e.target.value)}
              data-testid="lp-title"
              placeholder="Nome da campanha (não aparece publicamente se não usar em hero)"
              className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
          </div>
        </div>
        <div>
          <label className="text-xs text-neutral-500">Descrição interna</label>
          <textarea value={item.description} onChange={(e) => upd("description", e.target.value)}
            rows={2}
            className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
        </div>
        <div className="flex items-center gap-6">
          <label className="text-sm flex items-center gap-2" data-testid="lp-published-toggle">
            <input type="checkbox" checked={item.published || false} onChange={(e) => upd("published", e.target.checked)} />
            Publicada (ao vivo em <code>/lp/{item.slug || "…"}</code>)
          </label>
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={item.noindex || false} onChange={(e) => upd("noindex", e.target.checked)} />
            noindex (não aparecer no Google)
          </label>
        </div>
      </div>

      {/* SEO */}
      <div className="border border-black/10 rounded-md p-4 mb-4 space-y-3">
        <div className="text-sm uppercase tracking-widest font-semibold text-neutral-700 mb-1">SEO da página</div>
        <div className="grid grid-cols-2 gap-3">
          <input value={item.meta_title} onChange={(e) => upd("meta_title", e.target.value)}
            placeholder="Meta title (60 chars max)"
            className="border border-black/15 px-3 py-2 rounded text-sm" />
          <input value={item.og_image} onChange={(e) => upd("og_image", e.target.value)}
            placeholder="URL OG image (1200x630)"
            className="border border-black/15 px-3 py-2 rounded text-sm font-mono" />
        </div>
        <textarea value={item.meta_description} onChange={(e) => upd("meta_description", e.target.value)}
          placeholder="Meta description (150-160 chars ideal)" rows={2}
          className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
      </div>

      {/* Tracking */}
      <div className="border border-black/10 rounded-md p-4 mb-4 space-y-3">
        <div className="text-sm uppercase tracking-widest font-semibold text-neutral-700 mb-1">Tracking (opcional)</div>
        <div className="grid grid-cols-3 gap-3">
          <input value={item.ga_id} onChange={(e) => upd("ga_id", e.target.value)}
            placeholder="Google Analytics G-XXXX"
            className="border border-black/15 px-3 py-2 rounded text-sm font-mono" />
          <input value={item.meta_pixel_id} onChange={(e) => upd("meta_pixel_id", e.target.value)}
            placeholder="Meta Pixel ID"
            className="border border-black/15 px-3 py-2 rounded text-sm font-mono" />
          <input value={item.gtm_id} onChange={(e) => upd("gtm_id", e.target.value)}
            placeholder="GTM ID (GTM-XXXX)"
            className="border border-black/15 px-3 py-2 rounded text-sm font-mono" />
        </div>
        <input value={item.notify_emails} onChange={(e) => upd("notify_emails", e.target.value)}
          placeholder="Emails p/ notificar submissions (separados por vírgula)"
          className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
      </div>

      {/* Balãozinho WhatsApp + barra fixa mobile */}
      <div className="border border-black/10 rounded-md p-4 mb-4 space-y-3">
        <div className="text-sm uppercase tracking-widest font-semibold text-neutral-700 mb-1">Balãozinho WhatsApp + CTA fixo</div>
        <p className="text-xs text-neutral-500">Aparece depois que o visitante rola X% da página: barra fixa no rodapé (mobile) e balão verde flutuante no canto inferior direito (desktop). O número abaixo é o destino do clique.</p>
        <label className="text-sm flex items-center gap-2" data-testid="lp-sticky-enabled">
          <input type="checkbox" checked={item.sticky_cta?.enabled ?? false}
            onChange={(e) => upd("sticky_cta", { ...(item.sticky_cta || {}), enabled: e.target.checked })} />
          Ativar balãozinho / CTA fixo
        </label>
        {item.sticky_cta?.enabled && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-neutral-500">WhatsApp de destino (só números, com DDI)</label>
              <input value={item.sticky_cta?.whatsapp_phone || ""}
                onChange={(e) => upd("sticky_cta", { ...(item.sticky_cta || {}), whatsapp_phone: e.target.value })}
                data-testid="lp-sticky-whatsapp"
                placeholder="5562999999999"
                className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" />
              <p className="text-[10px] text-neutral-400 mt-1">Ex: 55 (Brasil) + 62 (DDD) + número. Sem espaços, parênteses ou traços.</p>
            </div>
            <div>
              <label className="text-xs text-neutral-500">Texto do botão (mobile)</label>
              <input value={item.sticky_cta?.label || ""}
                onChange={(e) => upd("sticky_cta", { ...(item.sticky_cta || {}), label: e.target.value })}
                placeholder="Quero falar sobre meu empreendimento"
                className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-neutral-500">Mensagem inicial já preenchida no WhatsApp</label>
              <input value={item.sticky_cta?.whatsapp_message || ""}
                onChange={(e) => upd("sticky_cta", { ...(item.sticky_cta || {}), whatsapp_message: e.target.value })}
                placeholder="Olá! Vim pela landing page da Places e quero conversar sobre um empreendimento."
                className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
            </div>
            <div>
              <label className="text-xs text-neutral-500">Aparece após rolar {item.sticky_cta?.scroll_percent ?? 30}% da página</label>
              <input type="range" min={0} max={90} step={5} value={item.sticky_cta?.scroll_percent ?? 30}
                onChange={(e) => upd("sticky_cta", { ...(item.sticky_cta || {}), scroll_percent: Number(e.target.value) })}
                className="w-full" />
            </div>
          </div>
        )}
      </div>

      {/* Bot Marina nesta LP */}
      <div className="border border-black/10 rounded-md p-4 mb-4 space-y-3">
        <div className="text-sm uppercase tracking-widest font-semibold text-neutral-700 mb-1">Bot Marina nesta LP</div>
        <p className="text-xs text-neutral-500">
          O mesmo balão de pré-atendimento da home (captura nome + telefone, conversa com IA e encaminha ao WhatsApp).
          O roteiro, avatar e prompt vêm de <strong>Chatbot Marina</strong>; aqui você só liga nesta LP e pode sobrescrever o número/mensagem.
          Os leads caem no <strong>Repositório de Leads</strong> e em <strong>Leads do Chatbot</strong>.
        </p>
        <label className="text-sm flex items-center gap-2" data-testid="lp-chatbot-enabled">
          <input type="checkbox" checked={item.chatbot?.enabled ?? false}
            onChange={(e) => upd("chatbot", { ...(item.chatbot || {}), enabled: e.target.checked })} />
          Exibir o bot Marina nesta landing page
        </label>
        {item.chatbot?.enabled && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-neutral-500">WhatsApp do bot — sobrescreve o global (opcional)</label>
              <input value={item.chatbot?.whatsapp_number || ""}
                onChange={(e) => upd("chatbot", { ...(item.chatbot || {}), whatsapp_number: e.target.value })}
                data-testid="lp-chatbot-whatsapp"
                placeholder="Vazio = usa o número do Chatbot Marina"
                className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" />
            </div>
            <div>
              <label className="text-xs text-neutral-500">Segundos até o balão aparecer (opcional)</label>
              <input type="number" min={0} value={item.chatbot?.delay_seconds ?? ""}
                onChange={(e) => upd("chatbot", { ...(item.chatbot || {}), delay_seconds: e.target.value === "" ? "" : Number(e.target.value) })}
                placeholder="Vazio = usa o padrão global"
                className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" />
            </div>
            <div className="col-span-2">
              <label className="text-xs text-neutral-500">Mensagem enviada ao WhatsApp — sobrescreve o global (opcional)</label>
              <input value={item.chatbot?.whatsapp_message_template || ""}
                onChange={(e) => upd("chatbot", { ...(item.chatbot || {}), whatsapp_message_template: e.target.value })}
                placeholder="Olá! Sou {name}, telefone {phone}. Vim pela LP {page}."
                className="w-full border border-black/15 px-3 py-2 rounded text-sm" />
              <p className="text-[10px] text-neutral-400 mt-1">Variáveis disponíveis: <code>{"{name}"}</code>, <code>{"{phone}"}</code>, <code>{"{interest}"}</code>, <code>{"{page}"}</code>.</p>
            </div>
          </div>
        )}
      </div>

      {/* Exit intent */}
      <div className="border border-black/10 rounded-md p-4 mb-4 space-y-3">
        <div className="text-sm uppercase tracking-widest font-semibold text-neutral-700 mb-1">Pop-up de saída (exit intent)</div>
        <p className="text-xs text-neutral-500">No desktop, quando o mouse sai pra cima da janela, abre um convite final com nome + WhatsApp. Aparece uma vez por sessão e só depois do tempo/rolagem definidos.</p>
        <label className="text-sm flex items-center gap-2" data-testid="lp-exit-enabled">
          <input type="checkbox" checked={item.exit_intent?.enabled ?? false}
            onChange={(e) => upd("exit_intent", { ...(item.exit_intent || {}), enabled: e.target.checked })} />
          Ativar pop-up de saída
        </label>
        {item.exit_intent?.enabled && (
          <div className="grid grid-cols-2 gap-3">
            <input value={item.exit_intent?.title || ""}
              onChange={(e) => upd("exit_intent", { ...(item.exit_intent || {}), title: e.target.value })}
              placeholder="Título do pop-up" className="border border-black/15 px-3 py-2 rounded text-sm" />
            <input value={item.exit_intent?.cta_label || ""}
              onChange={(e) => upd("exit_intent", { ...(item.exit_intent || {}), cta_label: e.target.value })}
              placeholder="Texto do botão" className="border border-black/15 px-3 py-2 rounded text-sm" />
            <textarea rows={2} value={item.exit_intent?.subtitle || ""}
              onChange={(e) => upd("exit_intent", { ...(item.exit_intent || {}), subtitle: e.target.value })}
              placeholder="Subtítulo" className="col-span-2 border border-black/15 px-3 py-2 rounded text-sm" />
            <div>
              <label className="text-xs text-neutral-500">Após {item.exit_intent?.delay_seconds ?? 45}s na página</label>
              <input type="range" min={0} max={180} step={5} value={item.exit_intent?.delay_seconds ?? 45}
                onChange={(e) => upd("exit_intent", { ...(item.exit_intent || {}), delay_seconds: Number(e.target.value) })}
                className="w-full" />
            </div>
            <div>
              <label className="text-xs text-neutral-500">Ou após rolar {item.exit_intent?.scroll_percent ?? 40}%</label>
              <input type="range" min={0} max={90} step={5} value={item.exit_intent?.scroll_percent ?? 40}
                onChange={(e) => upd("exit_intent", { ...(item.exit_intent || {}), scroll_percent: Number(e.target.value) })}
                className="w-full" />
            </div>
          </div>
        )}
      </div>

      {/* Header (sticky brand) */}
      <div className="border border-black/10 rounded-md p-4 mb-4 space-y-3">
        <div className="text-sm uppercase tracking-widest font-semibold text-neutral-700 mb-1">Marca no topo da página</div>
        <p className="text-xs text-neutral-500">Exibe a marca <strong>places</strong> no canto superior esquerdo, <strong>milimetricamente alinhada</strong> ao conteúdo do hero. Ela rola junto com a página (não fica fixa). O símbolo hexagonal ao lado mantém a animação característica da marca.</p>
        <label className="text-sm flex items-center gap-2" data-testid="lp-header-enabled">
          <input type="checkbox" checked={item.header?.enabled ?? false}
            onChange={(e) => upd("header", { ...(item.header || {}), enabled: e.target.checked })} />
          Exibir marca no topo da LP
        </label>
        {item.header?.enabled && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-neutral-500">Tipografia (contraste)</label>
              <select value={item.header?.brand_color || "white"}
                onChange={(e) => upd("header", { ...(item.header || {}), brand_color: e.target.value })}
                data-testid="lp-header-brand-color"
                className="w-full border border-black/15 px-2 py-2 rounded text-sm">
                <option value="white">Branca (para hero com imagem escura)</option>
                <option value="black">Preta (para hero com fundo claro)</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-neutral-500">Link ao clicar na marca</label>
              <input value={item.header?.link || "/"}
                onChange={(e) => upd("header", { ...(item.header || {}), link: e.target.value })}
                placeholder="/"
                className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" />
            </div>
            <div>
              <label className="text-xs text-neutral-500">Velocidade da animação (ms) — opcional</label>
              <input type="number" min={400} step={100}
                value={item.header?.logo_speed_ms || ""}
                onChange={(e) => upd("header", { ...(item.header || {}), logo_speed_ms: e.target.value ? Number(e.target.value) : "" })}
                placeholder="1600 (padrão)"
                data-testid="lp-header-logo-speed"
                className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" />
              <p className="text-[10px] text-neutral-400 mt-1">Tempo entre trocas do hexágono. Menor = mais rápido. Vazio usa o padrão do site (1600ms).</p>
            </div>
            <div className="col-span-2">
              <label className="text-xs text-neutral-500">Paleta de cores do hexágono — opcional</label>
              <input value={item.header?.logo_colors || ""}
                onChange={(e) => upd("header", { ...(item.header || {}), logo_colors: e.target.value })}
                placeholder="#FF5E00, #00B247, #FFD400 (padrão da marca)"
                data-testid="lp-header-logo-colors"
                className="w-full border border-black/15 px-3 py-2 rounded text-sm font-mono" />
              <p className="text-[10px] text-neutral-400 mt-1">Lista de cores separadas por vírgula (hex). Vazio usa laranja / verde / amarelo padrão. Útil pra campanhas com identidade visual própria.</p>
            </div>

            {/* Mini-preview animado — mostra o hexágono girando com a velocidade/paleta
                configuradas antes de salvar. */}
            <div className="col-span-2">
              <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500 mb-2">Prévia ao vivo</div>
              <div className={`rounded-md p-6 flex items-center justify-center border border-black/10 ${item.header?.brand_color === "black" ? "bg-neutral-100" : "bg-neutral-900"}`}
                data-testid="lp-brand-preview">
                <PlacesLogo
                  colorMode={item.header?.brand_color === "black" ? "light" : "dark"}
                  size="text-3xl md:text-4xl"
                  speedMs={item.header?.logo_speed_ms || undefined}
                  colors={parseColors(item.header?.logo_colors)}
                />
              </div>
              <p className="text-[10px] text-neutral-400 mt-1">O hexágono troca de forma e cor exatamente como vai aparecer na LP publicada.</p>
            </div>
          </div>
        )}
      </div>

      {/* Blocks */}
      <div className="border border-black/10 rounded-md p-4">
        <div className="text-sm uppercase tracking-widest font-semibold text-neutral-700 mb-1">Blocos da página</div>
        <p className="text-xs text-neutral-500 mb-3">Adicione blocos e arraste para reordenar. Comece com um Hero, adicione formulários e CTAs distribuídos, e feche com uma seção de conversão.</p>
        <LPBlocksEditor blocks={item.blocks || []} onChange={(v) => upd("blocks", v)} testidPrefix="lp-blocks" />
      </div>
    </div>
  );
}
